import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Users, Package, MessageSquare, CalendarDays, PauseCircle, PlayCircle, Monitor } from "lucide-react";
import { useAuth, authFetch } from "@/auth/AuthContext";
import TradeShowsAdmin from "./TradeShowsAdmin";
import SessionsModal from "./SessionsModal";

const TABS = [
  { key: "inquiries", label: "B2B Inquiries", icon: MessageSquare },
  { key: "samples", label: "Sample Requests", icon: Package },
  { key: "distributors", label: "Distributors", icon: Users },
  { key: "tradeshows", label: "Trade Shows", icon: CalendarDays },
];

const COLS = {
  inquiries: ["created_at", "full_name", "company", "email", "country", "message"],
  samples: ["created_at", "full_name", "company", "email", "country", "brands", "notes"],
  distributors: ["created_at", "name", "company", "email", "area", "city", "country", "status"],
};

const ENDPOINT = {
  inquiries: "/admin/inquiries",
  samples: "/admin/sample-requests",
  distributors: "/admin/distributors",
};

function formatCell(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "string" && v.length > 20 && v.includes("T")) {
    try {
      return new Date(v).toLocaleString();
    } catch { /* not a date */ }
  }
  if (typeof v === "string" && v.length > 80) return v.slice(0, 77) + "…";
  return String(v);
}

function StatusPill({ status }) {
  const paused = status === "paused";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-widest ${paused ? "bg-black/70 text-white" : "bg-emerald-100 text-emerald-800"}`}>
      {status || "active"}
    </span>
  );
}

export default function AdminDashboard() {
  const { user, token, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState({ inquiries: 0, samples: 0, distributors: 0 });
  const [sessionsFor, setSessionsFor] = useState(null); // distributor row or null

  const refreshUnread = async () => {
    const r = await authFetch(token, "/admin/unread");
    if (r.ok) setUnread(await r.json());
  };

  const toggleDistributorStatus = async (row) => {
    const next = row.status === "paused" ? "active" : "paused";
    if (next === "paused" && !window.confirm(`Pause ${row.name}? They won't be able to log in until reactivated.`)) return;
    const r = await authFetch(token, `/admin/distributors/${row.id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    if (r.ok) {
      setRows((rows) => rows.map((u) => (u.id === row.id ? { ...u, status: next } : u)));
    }
  };

  useEffect(() => {
    if (!user) return;
    refreshUnread();
    const onFocus = () => refreshUnread();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, token]);

  useEffect(() => {
    if (!user) return;
    if (tab === "tradeshows") { setLoading(false); return; }
    setLoading(true);
    authFetch(token, ENDPOINT[tab])
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        setRows(data);
        // Mark this tab as seen once its data is on screen, then refresh badges.
        return authFetch(token, "/admin/mark-seen", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kind: tab }),
        });
      })
      .then(() => refreshUnread())
      .finally(() => setLoading(false));
  }, [tab, user, token]);

  const downloadCsv = async () => {
    const r = await authFetch(token, `/admin/export.csv?kind=${tab}`);
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hupla-${tab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F7F5EE] text-[#0F1A12]" data-testid="admin-dashboard">
      <header className="bg-[#0F1A12] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <div>
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.32em] text-[#E8B14B]">Hupla · Admin</p>
            <h1 className="font-editorial italic text-2xl">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/60">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-xs hover:bg-white/5">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div className="flex gap-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              const count = unread[t.key] || 0;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-[#0F1A12] text-white" : "bg-white text-[#0F1A12]/70 hover:text-[#0F1A12]"}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  {count > 0 && (
                    <span
                      data-testid={`admin-tab-badge-${t.key}`}
                      className={`ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[0.65rem] font-bold ${active ? "bg-[#E8B14B] text-[#0F1A12]" : "bg-[#B02224] text-white"}`}
                      aria-label={`${count} unread ${t.label}`}
                    >
                      {count > 99 ? "99+" : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <button
            onClick={downloadCsv}
            disabled={tab === "tradeshows"}
            data-testid="admin-export-csv"
            className={`inline-flex items-center gap-2 rounded-full bg-[#E8B14B] text-[#0F1A12] px-4 py-2 text-sm font-semibold ${tab === "tradeshows" ? "opacity-30 pointer-events-none" : ""}`}
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {tab === "tradeshows" ? (
          <TradeShowsAdmin />
        ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          {loading ? (
            <p className="p-8 text-center text-sm text-black/50">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-black/50">No records yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0F1A12]/5 text-xs uppercase tracking-widest text-black/60">
                  <tr>
                    {COLS[tab].map((c) => (
                      <th key={c} className="px-4 py-3 text-left font-semibold">{c.replace(/_/g, " ")}</th>
                    ))}
                    {tab === "distributors" && (
                      <th className="px-4 py-3 text-left font-semibold">actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.id || i} className="border-t border-black/5 hover:bg-black/[0.02]">
                      {COLS[tab].map((c) => (
                        <td key={c} className="px-4 py-3 text-black/80 align-top">
                          {c === "status" ? <StatusPill status={row.status} /> : formatCell(row[c])}
                        </td>
                      ))}
                      {tab === "distributors" && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => setSessionsFor(row)}
                              data-testid={`admin-dist-sessions-${row.id}`}
                              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/15 text-black/70 hover:border-black hover:text-black transition-colors px-3 py-1.5 text-[0.65rem] font-bold uppercase tracking-widest"
                              title="View active sessions"
                            >
                              <Monitor className="w-3.5 h-3.5" /> Sessions
                            </button>
                            <button
                              onClick={() => toggleDistributorStatus(row)}
                              data-testid={`admin-dist-toggle-${row.id}`}
                              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[0.65rem] font-bold uppercase tracking-widest transition-colors ${row.status === "paused" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-black/80 text-white hover:bg-black"}`}
                            >
                              {row.status === "paused" ? <><PlayCircle className="w-3.5 h-3.5" /> Activate</> : <><PauseCircle className="w-3.5 h-3.5" /> Pause</>}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="px-4 py-3 text-xs text-black/50 border-t border-black/5">
            {rows.length} {rows.length === 1 ? "record" : "records"}
          </p>
        </div>
        )}
      </main>
      {sessionsFor && (
        <SessionsModal
          token={token}
          distributor={sessionsFor}
          onClose={() => setSessionsFor(null)}
        />
      )}
    </div>
  );
}
